/**
 * CLI access to organization Planning drafts and history.
 *
 * @module planning
 */

import process from 'node:process';
import { getArgValue, hasFlag, resolveConfig } from './auth.js';
import { apiRequest } from './cli-helpers.js';

type PlanningResponse = {
  ok: boolean;
  error?: string;
  planCycleId?: string;
  jobId?: string | null;
  plans?: Array<Record<string, unknown>>;
  plan?: Record<string, unknown>;
  pagination?: Record<string, unknown>;
};

function fail(message: string): never {
  console.error(`Error: ${message}`);
  process.exit(1);
}

function printHelp(): void {
  console.log(
    [
      'Usage: cofactor planning <sub-command> [options]',
      '',
      'Sub-commands:',
      '  trigger [--kind cycle|adhoc] [--instructions <text>]',
      '  list [--kind cycle|adhoc|release] [--status <status>] [--json]',
      '  show <plan-id> [--json]',
      '',
      'Options:',
      '  --env <env>       Target environment (prod, dev, local)',
      '  --api-url <url>   API URL override',
      '  --token <key>     API token override',
      '  --json            Print machine-readable JSON',
    ].join('\n')
  );
}

async function trigger(argv: string[], apiUrl: string, token: string) {
  const kind = getArgValue(argv, '--kind') ?? 'cycle';
  if (kind !== 'cycle' && kind !== 'adhoc') {
    fail('--kind must be cycle or adhoc');
  }
  const instructions = getArgValue(argv, '--instructions');
  const body: Record<string, unknown> = { kind };
  if (instructions) body.instructions = instructions;
  const result = await apiRequest<PlanningResponse>(apiUrl, token, 'POST', '/planning/plans', body);
  if (!result.ok) fail(result.error ?? 'Failed to trigger Planning');
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }
  console.log('Planning draft triggered');
  console.log(`  Plan ID: ${result.planCycleId}`);
  console.log(`  Job ID:  ${result.jobId}`);
}

async function list(argv: string[], apiUrl: string, token: string) {
  const query = new URLSearchParams();
  const kind = getArgValue(argv, '--kind');
  const status = getArgValue(argv, '--status');
  if (kind) query.set('kind', kind);
  if (status) query.set('status', status);
  const suffix = query.size > 0 ? `?${query}` : '';
  const result = await apiRequest<PlanningResponse>(
    apiUrl,
    token,
    'GET',
    `/planning/plans${suffix}`
  );
  if (!result.ok) fail(result.error ?? 'Failed to list Planning history');
  if (hasFlag(argv, '--json')) {
    console.log(
      JSON.stringify({ plans: result.plans ?? [], pagination: result.pagination }, null, 2)
    );
    return;
  }
  const plans = result.plans ?? [];
  if (plans.length === 0) {
    console.log('No plans found.');
    return;
  }
  for (const plan of plans) {
    const cycleNumber = typeof plan.cycleNumber === 'number' ? ` #${plan.cycleNumber}` : '';
    console.log(`${String(plan.id)}  ${String(plan.kind)}${cycleNumber}  ${String(plan.status)}`);
  }
}

async function show(argv: string[], apiUrl: string, token: string) {
  const id = argv[0];
  if (!id || id.startsWith('--')) fail('Missing plan ID');
  const result = await apiRequest<PlanningResponse>(
    apiUrl,
    token,
    'GET',
    `/planning/plans/${encodeURIComponent(id)}`
  );
  if (!result.ok) fail(result.error ?? 'Failed to load Planning detail');
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result.plan, null, 2));
    return;
  }
  const detail = result.plan ?? {};
  const cycle =
    detail.cycle && typeof detail.cycle === 'object'
      ? (detail.cycle as Record<string, unknown>)
      : {};
  console.log(`Plan ${String(cycle.id ?? id)}`);
  console.log(`  Kind:     ${String(cycle.kind ?? '')}`);
  console.log(`  Status:   ${String(cycle.status ?? '')}`);
  console.log(`  Revision: ${String(cycle.revisionCount ?? 0)}`);
  if (typeof cycle.summaryMd === 'string' && cycle.summaryMd) {
    console.log('');
    console.log(cycle.summaryMd);
  }
  const latest =
    detail.latestRevision && typeof detail.latestRevision === 'object'
      ? (detail.latestRevision as Record<string, unknown>)
      : null;
  if (latest && typeof latest.planDocMd === 'string') {
    console.log('');
    console.log(latest.planDocMd);
  }
}

export async function runPlanning(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;
  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }
  const { apiUrl, token } = await resolveConfig(argv);
  switch (subCommand) {
    case 'trigger':
      await trigger(rest, apiUrl, token);
      break;
    case 'list':
      await list(rest, apiUrl, token);
      break;
    case 'show':
      await show(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printHelp();
      process.exit(1);
  }
}
